import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { useSelector } from "react-redux";
import { LicensePlan } from "../../../store/actions/PlanActions";
import { RootState, useAppDispatch } from "../../../store";
import { hideSidePanel } from "../../../store/actions/sidePanelActions";
import Button from "../TailwindComponents/Button";
import {
  PiCheckLight,
  PiSparkleLight,
  PiUsersLight,
  PiBriefcaseLight,
  PiLightningLight,
  PiHardDriveLight,
  PiArrowUpLight,
  PiArrowDownLight,
  PiEnvelopeSimpleLight,
  PiShieldCheckLight,
  PiRocketLaunchLight,
  PiCrownLight,
  PiPackageLight,
  PiFunnelLight,
  PiFileTextLight,
  PiChartBarLight,
  PiBookOpenLight,
  PiPuzzlePieceLight,
  PiMinusLight,
  PiPlusLight,
} from "react-icons/pi";
import { IconType } from "react-icons";

const SECTION_LABELS: Record<string, string> = {
  dashboard: "Dashboard",
  tasks: "Tasks",
  calendar: "Calendar",
  notifications: "Notifications",
  messages: "Messages",
  contacts: "Contacts",
  matters: "Matters",
  files: "Files",
  projects: "Projects",
  people: "People",
  departments: "Departments",
  teams: "Teams",
  finance: "Finance",
  research: "AI Research",
  tables: "Tables AI",
  analytics: "Analytics",
  data_sources: "Knowledge Base",
  templates: "Templates",
  reviews: "Contract Review",
  finances: "Finances",
};

const AI_FEATURE_LABELS: Record<string, string> = {
  drafts: "AI Drafts",
  insights: "AI Insights",
  research: "AI Research",
  tables_ai: "Tables AI",
  onboarding_ai: "AI Onboarding",
  template_ai: "AI Templates",
  data_source_indexing: "Knowledge Indexing",
};

const getTierIcon = (planName: string): IconType => {
  const key = planName.toLowerCase();
  if (key.includes("starter")) return PiShieldCheckLight;
  if (key.includes("professional")) return PiRocketLaunchLight;
  if (key.includes("enterprise")) return PiCrownLight;
  return PiPackageLight;
};

const getAddonIcon = (name: string, sections: string[], aiFeatures: string[]): IconType => {
  const lower = name.toLowerCase();
  if (aiFeatures.length > 0 || lower.includes("ai") || sections.some((s) => ["research", "tables"].includes(s)))
    return PiSparkleLight;
  if (lower.includes("template") || sections.includes("templates")) return PiFileTextLight;
  if (lower.includes("pipeline") || sections.includes("client_pipeline")) return PiFunnelLight;
  if (lower.includes("analytics") || sections.includes("analytics")) return PiChartBarLight;
  if (lower.includes("knowledge") || sections.includes("data_sources")) return PiBookOpenLight;
  return PiPuzzlePieceLight;
};

/** Get the detailed description - prefer DB value, fallback to short description */
const getDetailedDescription = (plan: LicensePlan): string => {
  if (plan.detailed_description) return plan.detailed_description;
  if (plan.description) return plan.description;
  return plan.type === "addon"
    ? "This add-on extends your current plan with additional capabilities to help your team work more efficiently."
    : "This plan provides access to the platform's core features, enabling your team to manage clients, matters, and documents efficiently.";
};

/* ── Section grouping (must match PlanCard) ── */
const SECTION_GROUPS: { label: string; keys: string[] }[] = [
  { label: "Dashboard", keys: ["dashboard", "tasks", "notifications", "calendar", "messages"] },
  { label: "Workspace", keys: ["contacts", "matters", "files", "projects"] },
  { label: "Utilities", keys: ["research", "tables", "analytics", "data_sources", "templates"] },
  { label: "Organization", keys: ["people", "departments", "teams", "finance", "finances"] },
];

interface LicenseDetailPanelProps {
  plan: LicensePlan;
  billingPeriod: "monthly" | "annual";
  isCurrentPlan: boolean;
  isActive: boolean;
  onAction: (plan: LicensePlan, upgradeSeatCount?: number) => void;
  isLoading?: boolean;
  currentPlanSortOrder?: number;
  seatCount?: number;
  seatsUsed?: number;
  onUpdateSeats?: (newQuantity: number) => Promise<void>;
}

const LicenseDetailPanel: React.FC<LicenseDetailPanelProps> = ({
  plan,
  billingPeriod,
  isCurrentPlan,
  isActive,
  onAction,
  isLoading,
  currentPlanSortOrder,
  seatCount,
  seatsUsed,
  onUpdateSeats,
}) => {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const history = useHistory();
  const priceCurrency = useSelector((state: RootState) => state.license?.access?.price_currency ?? null);
  const formatPrice = (amount: number) =>
    new Intl.NumberFormat(i18n.language, {
      style: "currency",
      currency: priceCurrency || "EUR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  const gates = plan.feature_gates || { sections: [], ai_features: [], limits: {} };
  const price = billingPeriod === "monthly" ? plan.price_monthly : plan.price_annual;
  const isBase = plan.type === "base";

  const isDowngrade = isBase && !isCurrentPlan && currentPlanSortOrder !== undefined && plan.sort_order < currentPlanSortOrder;

  /* ── Seat control state (only for current base plan) ── */
  const [editedSeats, setEditedSeats] = useState<number | null>(null);
  const [seatLoading, setSeatLoading] = useState(false);

  /* ── Upgrade seat count (for non-current base plans) ── */
  const [upgradeSeatCount, setUpgradeSeatCount] = useState(1);

  const currentSeats = seatCount ?? 0;
  const usedSeats = seatsUsed ?? 0;
  const displaySeats = editedSeats ?? currentSeats;
  const seatDelta = displaySeats - currentSeats;
  const canDecrease = displaySeats > 1;
  const belowUsed = displaySeats < usedSeats;

  const Icon = isBase
    ? getTierIcon(plan.name || plan.display_name)
    : getAddonIcon(plan.name || plan.display_name, gates.sections, gates.ai_features);

  const handleContactSupport = () => {
    dispatch(hideSidePanel());
    history.push('/help/support?add=true');
  };

  const handleSeatUpdate = async () => {
    if (!onUpdateSeats || seatDelta === 0 || belowUsed) return;
    setSeatLoading(true);
    try {
      await onUpdateSeats(displaySeats);
      setEditedSeats(null);
      dispatch(hideSidePanel());
    } catch {
      // Error already shown by action
    } finally {
      setSeatLoading(false);
    }
  };

  const alreadyOwned = isBase ? isCurrentPlan : isActive;

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
        {/* Hero */}
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-lg bg-surface-sunken">
            <Icon className="h-5 w-5 text-fg-strong" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-medium text-fg-strong">
              {plan.display_name}
            </h3>
            <p className="text-sm text-fg-muted mt-1">
              {plan.description}
            </p>
          </div>
        </div>

        {/* Price */}
        <div className="border rounded-lg border-hairline p-4">
          <p className="text-xs text-fg-muted mb-2">{t("Pricing")}</p>
          <div className="flex items-baseline gap-2">
            <span className="inline-flex items-center gap-1 text-lg font-medium bg-success-100 dark:bg-success-900/40 text-success-700 dark:text-success-300 px-3 py-1.5 rounded-md">
              <span className="text-fg-strong">{formatPrice(price)}</span>
              <span className="text-sm font-normal">
                /{plan.is_per_seat ? t("seat") + "/" : ""}{t("mo")}
              </span>
            </span>
            {billingPeriod === "annual" && (
              <span className="text-xs text-fg-muted">{t("billed annually")}</span>
            )}
          </div>
          {billingPeriod === "annual" && plan.price_monthly > 0 && (
            <p className="text-xs text-success-600 dark:text-success-400 mt-2">
              {t("Save")} {Math.round(((plan.price_monthly - plan.price_annual) / plan.price_monthly) * 100)}% {t("compared to monthly billing")}
            </p>
          )}
          {plan.is_per_seat && (
            <p className="text-xs text-fg-muted mt-2">
              {t("Price is per seat. The total cost scales with the number of users in your organization.")}
            </p>
          )}
        </div>

        {/* Detailed Description */}
        <div>
          <p className="text-xs font-medium text-fg-strong mb-2">{t("About this " + (isBase ? "plan" : "add-on"))}</p>
          <p className="text-sm text-fg-muted leading-relaxed">
            {getDetailedDescription(plan)}
          </p>
        </div>

        {/* ── Seat Management (current base plan only) ── */}
        {isCurrentPlan && isBase && onUpdateSeats && seatCount != null && (
          <div className="border rounded-lg border-hairline p-4">
            <p className="text-xs font-medium text-fg-strong mb-3">{t("Seat Management")}</p>

            {/* Current usage */}
            <div className="flex items-center justify-between text-sm mb-3">
              <span className="text-fg-muted">{t("Seats in use")}</span>
              <span className="text-fg-strong">
                {usedSeats} / {currentSeats}
              </span>
            </div>

            {/* Seat counter */}
            <div className="flex items-center gap-4">
              <button
                onClick={() => setEditedSeats(Math.max(1, displaySeats - 1))}
                disabled={!canDecrease}
                className="p-2 rounded-lg border border-hairline hover:bg-surface-sunken/70 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                <PiMinusLight className="h-5 w-5 text-fg-muted" />
              </button>
              <div className="flex-1 text-center">
                <span className="text-3xl font-semibold text-fg-strong">{displaySeats}</span>
                <p className="text-xs text-fg-muted mt-0.5">{t("seats")}</p>
              </div>
              <button
                onClick={() => setEditedSeats(displaySeats + 1)}
                className="p-2 rounded-lg border border-hairline hover:bg-surface-sunken/70 transition-colors"
              >
                <PiPlusLight className="h-5 w-5 text-fg-muted" />
              </button>
            </div>

            {/* Delta message */}
            {seatDelta !== 0 && (
              <div className={`mt-3 rounded-lg border p-2.5 text-xs ${
                belowUsed
                  ? "border-danger-200 dark:border-danger-800 bg-danger-50 dark:bg-danger-900/20 text-danger-600 dark:text-danger-400"
                  : seatDelta > 0
                  ? "border-info-200 dark:border-info-800 bg-info-50 dark:bg-info-900/20 text-info-700 dark:text-info-300"
                  : "border-warning-200 dark:border-warning-800 bg-warning-50 dark:bg-warning-900/20 text-warning-700 dark:text-warning-300"
              }`}>
                {belowUsed
                  ? `${usedSeats} seat(s) are currently assigned. You must unassign users before reducing below ${usedSeats}.`
                  : seatDelta > 0
                  ? `Adding ${seatDelta} seat(s). You will be charged immediately for the prorated amount.`
                  : `Removing ${Math.abs(seatDelta)} seat(s). A credit will be applied to your account.`
                }
              </div>
            )}

            {/* Action button */}
            {seatDelta !== 0 && (
              <div className="mt-3">
                <Button
                  variant={seatDelta > 0 ? "primary" : "outline"}
                  size="small"
                  onClick={handleSeatUpdate}
                  disabled={belowUsed || seatLoading}
                  isLoading={seatLoading}
                  className="w-full"
                >
                  {seatDelta > 0 ? t("Update") : t("Remove Seat(s)")}
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Limits */}
        {Object.keys(gates.limits).length > 0 && (
          <div>
            <p className="text-xs font-medium text-fg-strong mb-3">{t("Limits & Quotas")}</p>
            <div className="grid grid-cols-2 gap-3">
              {gates.limits.max_seats !== undefined && (
                <div className="flex items-center gap-2 p-3 rounded-lg border border-hairline">
                  <PiUsersLight className="h-4 w-4 text-fg-strong flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-fg-strong">
                      {gates.limits.max_seats === null ? t("Unlimited") : gates.limits.max_seats}
                    </p>
                    <p className="text-xs text-fg-muted">{t("Seats")}</p>
                  </div>
                </div>
              )}
              {gates.limits.max_matters !== undefined && (
                <div className="flex items-center gap-2 p-3 rounded-lg border border-hairline">
                  <PiBriefcaseLight className="h-4 w-4 text-fg-strong flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-fg-strong">
                      {gates.limits.max_matters === null ? t("Unlimited") : gates.limits.max_matters}
                    </p>
                    <p className="text-xs text-fg-muted">{t("Matters")}</p>
                  </div>
                </div>
              )}
              {gates.limits.max_ai_queries_monthly !== undefined && (
                <div className="flex items-center gap-2 p-3 rounded-lg border border-hairline">
                  <PiLightningLight className="h-4 w-4 text-fg-strong flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-fg-strong">
                      {gates.limits.max_ai_queries_monthly === null ? t("Unlimited") : `${gates.limits.max_ai_queries_monthly}/mo`}
                    </p>
                    <p className="text-xs text-fg-muted">{t("AI Queries")}</p>
                  </div>
                </div>
              )}
              {gates.limits.max_storage_gb !== undefined && (
                <div className="flex items-center gap-2 p-3 rounded-lg border border-hairline">
                  <PiHardDriveLight className="h-4 w-4 text-fg-strong flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-fg-strong">
                      {gates.limits.max_storage_gb === null ? t("Unlimited") : `${gates.limits.max_storage_gb} GB`}
                    </p>
                    <p className="text-xs text-fg-muted">{t("Storage")}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Sections (features included) - grouped */}
        {gates.sections.length > 0 && (
          <div>
            <p className="text-xs font-medium text-fg-strong mb-3">{t("Included Features")}</p>
            <div className="space-y-3">
              {SECTION_GROUPS
                .map((group) => ({
                  label: group.label,
                  items: group.keys.filter((k) => new Set(gates.sections).has(k)),
                }))
                .filter((g) => g.items.length > 0)
                .map((group) => (
                  <div key={group.label}>
                    <p className="text-[10px] uppercase tracking-wider text-fg-muted mb-1.5">
                      {t(group.label)}
                    </p>
                    <ul className="grid grid-cols-2 gap-x-4 gap-y-1.5">
                      {group.items.map((s) => (
                        <li key={s} className="flex items-center gap-2 text-sm text-fg-body">
                          <PiCheckLight className="h-4 w-4 text-success-600 dark:text-success-400 flex-shrink-0" />
                          {t(SECTION_LABELS[s] || s)}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
            </div>
          </div>
        )}

        {/* AI Features */}
        {gates.ai_features.length > 0 && (
          <div>
            <p className="text-xs font-medium text-fg-strong mb-3">{t("AI Capabilities")}</p>
            <ul className="space-y-2">
              {gates.ai_features.map((f) => (
                <li key={f} className="flex items-center gap-2 text-sm text-fg-body">
                  <PiSparkleLight className="h-4 w-4 text-fg-strong flex-shrink-0" />
                  {t(AI_FEATURE_LABELS[f] || f)}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Footer actions */}
      <div className="border-t border-hairline px-6 py-4 space-y-3">
        {/* Seat stepper for upgrade/new base plans */}
        {isBase && !alreadyOwned && (
          <div className="flex items-center justify-between">
            <span className="text-xs text-fg-muted">{t("Seats")}</span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setUpgradeSeatCount(Math.max(1, upgradeSeatCount - 1))}
                disabled={upgradeSeatCount <= 1}
                className="p-1 rounded border border-hairline hover:bg-surface-sunken/70 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                <PiMinusLight className="h-3.5 w-3.5 text-fg-muted" />
              </button>
              <span className="text-sm font-medium text-fg-strong w-8 text-center">{upgradeSeatCount}</span>
              <button
                onClick={() => setUpgradeSeatCount(upgradeSeatCount + 1)}
                className="p-1 rounded border border-hairline hover:bg-surface-sunken/70 transition-colors"
              >
                <PiPlusLight className="h-3.5 w-3.5 text-fg-muted" />
              </button>
            </div>
          </div>
        )}

        <div className="flex items-center justify-between">
          <Button
            variant={alreadyOwned ? "neutral" : isDowngrade ? "outline" : "primary"}
            size="small"
            onClick={() => onAction(plan, isBase && !alreadyOwned ? upgradeSeatCount : undefined)}
            disabled={alreadyOwned || isLoading}
            isLoading={isLoading}
            icon={alreadyOwned ? undefined : isDowngrade ? <PiArrowDownLight /> : <PiArrowUpLight />}
          >
            {alreadyOwned
              ? isBase ? t("Current Plan") : t("Already Added")
              : isBase
                ? isDowngrade ? t("Downgrade to this plan") : t("Upgrade to this plan")
                : t("Add to subscription")}
          </Button>
          <Button
            variant="outline"
            size="small"
            onClick={handleContactSupport}
            icon={<PiEnvelopeSimpleLight />}
          >
            {t("Contact Support")}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default LicenseDetailPanel;
